import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import MoleculePaginationSimple from '../../Molecules/MoleculePaginationSimple/MoleculePaginationSimple';
import AtomNumberDisplay from '../../Atoms/AtomNumberDisplay/AtomNumberDisplay';
import AtomCheckbox from '../../Atoms/AtomCheckbox/AtomCheckbox';
import { Button, Icon, Text } from '../../Atoms';
import { COLOR_BUTTON } from '../../Config/Config';
import { apiGetPayment } from '../../Api/Payment/Payment';

const OrganismFilterableSelectableTable = ({
                                             data,
                                             columnTitles,
                                             onSelectionChange = () => {}
                                           }) => {
  // Nombre d'éléments par page fixé à 5
  const [itemsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [paymentMode, setPaymentMode] = useState('total');

  // Filtrer les données selon item.montant
  // et Tri des échéances (les plus anciennes en premier)
  const filteredData = useMemo(() => {
    console.log('data', data);
    return [...data]
      .filter(item => parseFloat(item.montant) > 0)
        .sort((a, b) => new Date(a.echeanceDebut) - new Date(b.echeanceDebut));
  }, [data]);

  // Calculer le nombre total de pages
  const totalPages = useMemo(() => Math.ceil(filteredData.length / itemsPerPage), [filteredData, itemsPerPage]);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Réinitialiser la page courante si les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, []);

  // Présélectionner toutes les lignes par défaut lors du changement des filtres
  useEffect(() => {
    const allIds = filteredData.map(item => item.id);
    setSelectedRows(allIds);
    setPaymentMode('total');
  }, [filteredData]);

  // Calculer les totaux des montants sélectionnés
  const totals = useMemo(() => {
    const selectedItems = filteredData.filter(item => selectedRows.includes(item.id));
    return {
      montantTotal: selectedItems.reduce((sum, item) => sum + (Number(item.montant) || 0), 0),
      montantDuTotal: selectedItems.reduce((sum, item) => sum + (Number(item.montantDu) || 0), 0),
      montantRegleTotal: selectedItems.reduce((sum, item) => sum + (Number(item.montantRegle) || 0), 0)
    };
  }, [filteredData, selectedRows]);

  // Construire l'objet à envoyer au parent via onSelectionChange
  const dataToSend = useMemo(() => {
    const selectedItems = filteredData.filter(item => selectedRows.includes(item.id));
    return {
      amount: totals.montantTotal,
      nbMaturities: selectedRows.length,
      origin: "Web",
      paymentOrderDetails: selectedItems.map(item => ({
        amount: Number(item.montant) || 0,
        codePolice: item.codePolice,
        idMaturity: item.appelcotis
      }))
    };
  }, [filteredData, selectedRows, totals]);

  //  Gestionnaire pour sélectionner/désélectionner une ligne avec sélection ascendante en mode partiel
  const handleRowSelection = useCallback((id) => {
    setSelectedRows(prev => {
      // Si le mode de paiement n'est pas "partial", on laisse le comportement par défaut
      // (par exemple, en cas de basculement depuis le mode "total")
      if (paymentMode !== 'partial') {
        // On force le passage en mode partiel dès qu'une sélection manuelle intervient
        setPaymentMode('partial');
        // Pour respecter la sélection ascendante, on n'autorise que la première échéance non sélectionnée
        const sortedNotSelected = filteredData.filter(item => !prev.includes(item.id));
        if (sortedNotSelected.length && sortedNotSelected[0].id === id) {
          return [...prev, id];
        }
        // Sinon, on ne modifie rien
        return prev;
      }

      // En mode partiel, on respecte l'ordre chronologique
      const sortedSelected = filteredData.filter(item => prev.includes(item.id));
      const sortedNotSelected = filteredData.filter(item => !prev.includes(item.id));
      const isSelected = prev.includes(id);

      if (isSelected) {
        // Pour désélectionner, seule la dernière échéance sélectionnée peut être retirée
        const lastSelected = sortedSelected[sortedSelected.length - 1];
        if (lastSelected && lastSelected.id === id) {
          return prev.filter(itemId => itemId !== id);
        }
        // Tentative de désélection non autorisée, on ne change rien
        return prev;
      } else {
        // Pour sélectionner, on autorise uniquement la première échéance non sélectionnée
        const firstNotSelected = sortedNotSelected[0];
        if (firstNotSelected && firstNotSelected.id === id) {
          return [...prev, id];
        }
        // Si l'échéance cliquée n'est pas la prochaine attendue, on ne change rien
        return prev;
      }
    });
  }, [filteredData, paymentMode]);

  // Gestionnaire pour sélectionner toutes les lignes
  const handleSelectAllTotal = useCallback(() => {
    const allIds = filteredData.map(item => item.id);
    setSelectedRows(allIds);
    setPaymentMode('total');
  }, [filteredData]);

  // Gestionnaire pour désélectionner toutes les lignes
  const handleSelectAllPartial = useCallback(() => {
    const firstItemId = filteredData.length > 0 ? [filteredData[0].id] : [];
    setSelectedRows(firstItemId);
    setPaymentMode('partial');
  }, []);

  // Notifier le parent des changements de sélection
  useEffect(() => {
    onSelectionChange(dataToSend);
  }, [dataToSend, onSelectionChange]);

  // Réinitialiser la page si la page courante dépasse le nombre total de pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Gestionnaire d'appel API pour créer une commande de paiement et rediriger l'utilisateur via callback
  const handleCallApiCreatePaymentOrder = useCallback(async () => {
    try {
      if (totals.montantTotal > 0) {
        const response = await apiGetPayment(dataToSend);
        console.log('Paiement effectué response et dataToSend:', response, dataToSend);

        if (response.paymentUrl && response.paymentUrl.length > 0) {
          window.location.href = response.paymentUrl;
        } else {
          setSelectedRows([]);
          setPaymentMode('partial');
        }
      }
    } catch (error) {
      console.error('Erreur lors du paiement:', error);
    }
  }, [dataToSend, totals]);

  return (
      <div className="w-full">
        {/* Boutons de sélection de paiement */}
        <div className="container mb-20">
          <div className="row d-flex justify-content-between align-items-stretch">

            {filteredData.length > 1 && (
                <div className="col-6 p-2 d-flex">
                  <button
                      type="button"
                      onClick={handleSelectAllTotal}
                      className={`p-3 bg-white border rounded shadow d-flex flex-row justify-content-between align-items-lg-center flex-grow-1 ${paymentMode === 'total' ? 'border-danger' : 'pointer-cursor'}`}
                  >
                    <div className="d-flex flex-column">
                      <Text
                          text="Payer la totalité"
                          type="h8"
                          className="p-1 font-weight-bold text-left"
                      />
                  <p className="p-1 text-left">Payer la totalité pour éviter tout risque de résiliation de votre contrat</p>
                    </div>
                    <div>
                      <Icon
                          size="20"
                          icon="SortTop"
                          className="faq-icon-next-step"
                      />
                    </div>
                  </button>
                </div>
            )}

            <div className="col-6 p-2 d-flex">
              {filteredData.length > 1 && (
                  <button
                      type="button"
                      onClick={handleSelectAllPartial}
                      className={`p-3 bg-white border rounded shadow d-flex flex-row justify-content-between align-items-lg-center flex-grow-1 ${paymentMode === 'partial' ? 'border-danger' : 'pointer-cursor'}`}
                  >
                    <div className="d-flex flex-column">
                      <Text
                          text="Payer partiellement"
                          type="h8"
                          className="p-1 font-weight-bold text-left"
                      />
                      <p className="p-1 text-left">
                        Le montant restant de votre règlement sera inclus dans le montant de votre prochaine cotisation.
                      </p>
                    </div>
                    <div>
                      <Icon
                          size="20"
                          icon="SortTop"
                          className="faq-icon-next-step"
                      />
                    </div>
                  </button>
              )}
            </div>
          </div>
        </div>

        {/* Message d'attention */}
        <table className="table table-responsive table-hover w-full">
          <thead className="thead-muted">
          <tr>
            <th className="font-weight-normal small bg-light text-secondary">
              SÉLECTIONNÉ
            </th>
            {columnTitles.map(title => (
                <th key={title} scope="col" className="font-weight-normal small bg-light text-secondary">
                  {title}
                </th>
            ))}
          </tr>
          </thead>
          <tbody>
          {currentData.map(item => {
            const isSelected = selectedRows.includes(item.id);
            return (
                <tr key={item.id} className={isSelected ? 'table-selected' : ''}>
                  <td>
                    <AtomCheckbox
                        checked={isSelected}
                        onClick={() => handleRowSelection(item.id)}
                        type="classique"
                    />
                  </td>
                  <td>{item.codePolice}</td>
                  <td>{item.dateReglement}</td>
                  <td>
                    <div className={`echeance echeance-${item.color}`}>
                      {item.echeanceDebut}
                      <br />
                      au {item.echeanceFin}
                    </div>
                  </td>
                  <td className="text-right">
                    <AtomNumberDisplay value={item.montant || 0} withCurrency={true} />
                  </td>
                  <td className="text-right">
                    <AtomNumberDisplay value={item.montantDu || 0} withCurrency={true} />
                  </td>
                  <td className="text-right">
                    <AtomNumberDisplay value={item.montantRegle || 0} withCurrency={true} />
                  </td>
                  <td>{item.remarque}</td>
                  <td className="text-right">
                    <AtomNumberDisplay value={item.montantFrais || 0} withCurrency={true} />
                  </td>
                  <td>{item.statut}</td>
                </tr>
            );
          })}
          </tbody>
        </table>

        {/* Affichage si aucune donnée ne correspond aux filtres */}
        {filteredData.length === 0 && (
            <div className="text-center py-3">
              Aucune donnée ne correspond aux filtres sélectionnés.
            </div>
        )}

        {/* Pagination si nécessaire */}
        {filteredData.length > itemsPerPage && (
            <MoleculePaginationSimple
                currentPage={currentPage}
                totalPages={totalPages}
                onPrevious={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                onNext={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            />
        )}

        {/* Section récapitulative du montant sélectionné */}
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div>
          <span className="mr-3">
            <strong>
              Montant sélectionné à payer : <AtomNumberDisplay value={totals.montantTotal || 0} withCurrency={true} />
            </strong>
          </span>
          </div>
        </div>

        {/* Bouton de validation du paiement */}
        <div className="align-self-center mb-20">
          <Button
              text="Je règle ma facture"
              disabled={totals.montantTotal <= 0}
              className="button-big-round"
              onClick={handleCallApiCreatePaymentOrder}
              color={totals.montantTotal <= 0 ? COLOR_BUTTON.SALMON : COLOR_BUTTON.SECOND_RED}
          />
        </div>
      </div>
  );
};

OrganismFilterableSelectableTable.propTypes = {
  data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        codePolice: PropTypes.string,
        dateReglement: PropTypes.string,
        color: PropTypes.string,
        echeanceDebut: PropTypes.string,
        echeanceFin: PropTypes.string,
        montant: PropTypes.string,
        montantDu: PropTypes.number,
        montantRegle: PropTypes.number,
        remarque: PropTypes.string,
        montantFrais: PropTypes.number,
        statut: PropTypes.string,
        appelcotis: PropTypes.string,
      })
  ).isRequired,
  columnTitles: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectionChange: PropTypes.func
};

export default OrganismFilterableSelectableTable;
